import React, { useState } from 'react';
import { HelpCircle, Loader2, Send, Zap } from 'lucide-react';

import type { QuestionCadrage } from '../../services/api/assistants';

interface Props {
  comprehension: string;
  questions: QuestionCadrage[];
  /** Envoie les réponses ; `forcer` demande de produire sans attendre. */
  onRepondre: (reponse: string, forcer: boolean) => void;
  occupe: boolean;
}

/**
 * Précisions demandées avant rédaction.
 *
 * Sans cette étape, l'assistant rendait un document criblé de « à compléter »
 * et réclamait les données à la fin : l'agent avait attendu une minute pour
 * obtenir un formulaire. On pose les questions d'abord.
 *
 * Répondre à tout n'est pas obligatoire : les champs laissés vides sont
 * simplement omis, et l'on peut demander à produire immédiatement.
 */
const QuestionsCadrage: React.FC<Props> = ({ comprehension, questions, onRepondre, occupe }) => {
  const [reponses, setReponses] = useState<string[]>(() => questions.map(() => ''));

  /** Reprend chaque question suivie de sa réponse, pour garder le lien. */
  const composer = () =>
    questions
      .map((q, i) => [q.question, reponses[i].trim()])
      .filter(([, reponse]) => reponse)
      .map(([question, reponse]) => `${question}\n${reponse}`)
      .join('\n\n');

  const auMoinsUne = reponses.some((r) => r.trim());

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <HelpCircle className="mt-0.5 h-5 w-5 shrink-0 text-eg-accent" />
        <div>
          <p className="font-medium text-eg-encre">
            Quelques précisions avant de rédiger
          </p>
          {comprehension && (
            <p className="mt-1 text-sm text-eg-gris">{comprehension}</p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {questions.map((q, index) => (
          <div key={index} className="rounded-md border border-eg-bord bg-eg-blanc p-4">
            <p className="text-sm font-medium text-eg-encre">{q.question}</p>
            {q.pourquoi && (
              <p className="mt-0.5 text-xs text-eg-gris">{q.pourquoi}</p>
            )}
            <input
              type="text"
              value={reponses[index]}
              onChange={(e) =>
                setReponses((precedentes) =>
                  precedentes.map((r, i) => (i === index ? e.target.value : r)),
                )
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' && auMoinsUne && !occupe) {
                  onRepondre(composer(), false);
                }
              }}
              disabled={occupe}
              placeholder={q.exemple ? `Ex. : ${q.exemple}` : 'Votre réponse...'}
              className="mt-2 w-full rounded-md border border-[#DDE2DD] px-3 py-2 text-sm
                focus:border-eg-accent focus:outline-none focus:ring-1 focus:ring-eg-accent
                disabled:opacity-50"
            />
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => onRepondre(composer(), false)}
          disabled={occupe || !auMoinsUne}
          className="inline-flex items-center gap-2 rounded-md bg-eg-ombre px-4 py-2 text-sm
            font-medium text-eg-blanc transition-colors hover:bg-eg-encre disabled:opacity-50"
        >
          {occupe ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Envoyer mes réponses
        </button>

        <button
          type="button"
          onClick={() => onRepondre('Produis avec les éléments dont tu disposes.', true)}
          disabled={occupe}
          className="inline-flex items-center gap-2 rounded-md border border-[#DDE2DD] px-4 py-2
            text-sm text-eg-gris transition-colors hover:bg-eg-papier disabled:opacity-50"
        >
          <Zap className="h-4 w-4" />
          Rédiger sans attendre
        </button>

        <span className="text-xs text-eg-gris">
          Les questions laissées sans réponse seront traitées en hypothèses, signalées
          dans le document.
        </span>
      </div>
    </div>
  );
};

export default QuestionsCadrage;
